import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, MessageCircle, Phone, Share2, MapPin, Users, Clock, Send, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August",
  "September", "Oktober", "November", "Dezember"];
const WD = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];

const targetDate = (d, t) => {
  if (!d) return null;
  const [y, m, day] = d.split("-").map(Number);
  const [hh, mm] = (t || "18:00").split(":").map(Number);
  return new Date(y, m - 1, day, hh || 18, mm || 0, 0);
};

const Unit = ({ value, label }) => (
  <div className="text-center min-w-[66px] sm:min-w-[92px]">
    <div className="font-serif text-4xl sm:text-6xl text-[#FAF3E8] tabular-nums leading-none">
      {String(value).padStart(2, "0")}
    </div>
    <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.25em] text-[#C9A392] mt-2">{label}</div>
  </div>
);

const Ornament = ({ className = "" }) => (
  <div className={`flex items-center justify-center gap-3 mt-5 ${className}`}>
    <span className="w-16 h-px bg-[#BA6D52]/50" />
    <span className="w-2 h-2 rotate-45 border border-[#BA6D52]" />
    <span className="w-16 h-px bg-[#BA6D52]/50" />
  </div>
);

export default function EventSeite() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [now, setNow] = useState(Date.now());
  const [wunsch, setWunsch] = useState({ name: "", personen: "", text: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/event-seiten/${slug}`);
        setData(d);
      } catch {
        setError("Diese Seite gibt es nicht — bitte prüfen Sie den Link.");
      }
    })();
  }, [slug]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const target = useMemo(() => targetDate(data?.event_date, data?.event_time), [data]);
  const left = target ? target.getTime() - now : 0;
  const past = left <= 0;
  const days = Math.floor(left / 86400000);
  const hours = Math.floor((left % 86400000) / 3600000);
  const mins = Math.floor((left % 3600000) / 60000);
  const secs = Math.floor((left % 60000) / 1000);

  const dateLong = useMemo(() => {
    if (!data?.event_date) return "";
    const [y, m, d] = data.event_date.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return `${WD[dt.getDay()]}, ${d}. ${MONTHS[m - 1]} ${y}`;
  }, [data]);

  const share = () => {
    const link = data.share_url || data.url;
    const text = `Unser kulinarischer Abend mit Privatkoch Lukas Stutz — hier das Menü und der Countdown: ${link}`;
    if (navigator.share) {
      navigator.share({ title: data.anlass, text, url: link }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
    }
  };

  const copy = async () => {
    const link = data.share_url || data.url;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link kopiert");
    } catch {
      window.prompt("Link kopieren:", link);
    }
  };

  const sendWunsch = async () => {
    if (!wunsch.text.trim()) { toast.error("Bitte schreiben Sie kurz, worum es geht"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/event-seiten/${slug}/wuensche`, wunsch);
      setSent(true);
      setWunsch({ name: "", personen: "", text: "" });
      toast.success("Danke! Ihre Nachricht ist bei Lukas Stutz angekommen.");
    } catch (err) {
      toast.error("Das hat nicht funktioniert — bitte nochmal versuchen.");
    } finally {
      setSending(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#FDF8F1] flex items-center justify-center px-6">
        <p className="font-serif text-2xl text-[#2B2320] text-center" data-testid="event-error">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#FDF8F1] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#BA6D52]" size={30} />
      </div>
    );
  }

  const FIELD = "mt-2 w-full bg-white border border-[#E8DCCB] focus:outline-none focus:border-[#BA6D52] px-4 py-3 text-sm text-[#2B2320] transition-colors";
  const LBL = "text-[10px] uppercase tracking-[0.2em] text-[#6E5B4C]";

  return (
    <div data-testid="page-event" className="min-h-screen bg-[#FDF8F1] text-[#2B2320]">
      {/* ---------- Kopf: Logo auf hellem Band, getrennt vom Foto ---------- */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center"
             style={{ backgroundImage: "url('/event-bg-teller.jpg')" }} />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(20,18,14,0.72)" }} />
        <div className="absolute inset-0"
             style={{ background: "radial-gradient(ellipse at 50% 45%, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.15) 70%)" }} />
        <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-b from-transparent to-[#FDF8F1]" />
        <div className="relative max-w-3xl mx-auto px-6 pt-10 pb-20 text-center">
          <img src="/logo.png" alt="Lukas Stutz"
            className="h-16 w-16 md:h-20 md:w-20 mx-auto rounded-full bg-[#FAF8F3]/90 p-1.5" />
          <p className="text-[10px] uppercase tracking-[0.35em] text-[#E2C4B2] mt-4">
            Lukas Stutz · Privatkoch
          </p>

          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#FFFBF4] mt-12 drop-shadow-[0_3px_16px_rgba(0,0,0,0.9)]"
              data-testid="event-anrede">
            Guten Tag{data.anrede ? ` ${data.anrede},` : ","}
          </h1>
          <p className="text-base md:text-lg text-[#F2E7DA] mt-6 font-light leading-relaxed drop-shadow-[0_2px_10px_rgba(0,0,0,0.85)]"
             data-testid="event-gruss">
            {data.gruss
              || `Ihr ${data.anlass} steht bevor. Hier finden Sie alles zum Abend — und wie lange es noch dauert.`}
          </p>

          <div className="mt-12 bg-black/50 backdrop-blur-md border border-white/15 px-5 py-10"
               data-testid="event-countdown">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#E2C4B2]">
              {past ? "Der Abend hat begonnen" : "Es sind noch"}
            </p>
            <div className="flex items-start justify-center gap-3 sm:gap-10 mt-6">
              {past ? (
                <p className="font-serif text-3xl sm:text-4xl text-[#FAF3E8]">Guten Appetit!</p>
              ) : (
                <>
                  <Unit value={days} label="Tage" />
                  <Unit value={hours} label="Stunden" />
                  <Unit value={mins} label="Minuten" />
                  <Unit value={secs} label="Sekunden" />
                </>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-x-7 gap-y-3 mt-8 text-sm text-[#EADFD2]">
            <span className="inline-flex items-center gap-2"><Clock size={14} className="text-[#D99A5B]" />
              {dateLong}{data.event_time ? ` · ${data.event_time} Uhr` : ""}</span>
            {data.ort && <span className="inline-flex items-center gap-2"><MapPin size={14} className="text-[#D99A5B]" />{data.ort}</span>}
            {data.gaeste > 0 && <span className="inline-flex items-center gap-2"><Users size={14} className="text-[#D99A5B]" />{data.gaeste} Gäste</span>}
          </div>
        </div>
      </section>

      {/* ---------- Menü als große Menükarte ---------- */}
      <section className="bg-[#FDF8F1]" data-testid="event-menu">
        <div className="max-w-2xl mx-auto px-5 sm:px-6 py-16 md:py-20">
          <div className="relative bg-[#FFFDF8] border border-[#E6D8C2] shadow-[0_26px_60px_-38px_rgba(120,80,48,0.55)] px-6 sm:px-12 py-14 sm:py-16">
            {/* Eckmarken wie auf der gedruckten Karte */}
            <span className="absolute top-4 left-4 w-5 h-5 border-t border-l border-[#BA6D52]/60" />
            <span className="absolute top-4 right-4 w-5 h-5 border-t border-r border-[#BA6D52]/60" />
            <span className="absolute bottom-4 left-4 w-5 h-5 border-b border-l border-[#BA6D52]/60" />
            <span className="absolute bottom-4 right-4 w-5 h-5 border-b border-r border-[#BA6D52]/60" />

            <div className="text-center">
              <p className="text-[11px] sm:text-xs tracking-[0.45em] text-[#5E5147]">L U K A S&nbsp;&nbsp;S T U T Z</p>
              <Ornament />
              <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tight">Menükarte</h2>
              {data.untertitel && <p className="text-sm sm:text-base text-[#5E5147] italic mt-4">{data.untertitel}</p>}
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#BA6D52] mt-5">
                {data.anlass}
              </p>
              <p className="text-[11px] text-[#6E5B4C] mt-2">{dateLong}</p>
              <Ornament className="mt-8" />
            </div>

            <div className="mt-10 space-y-9">
              {(data.gaenge || []).map((g, i) => (
                <div key={i} className="text-center" data-testid={`event-gang-${i}`}>
                  {g.label && (
                    <p className="text-[9px] uppercase tracking-[0.3em] text-[#BA6D52]">{g.label}</p>
                  )}
                  <p className="font-serif text-xl md:text-2xl mt-3 leading-snug">{g.titel}</p>
                  {g.beschreibung && (
                    <p className="text-sm text-[#4A3F36] mt-2 font-light italic">{g.beschreibung}</p>
                  )}
                  {i < (data.gaenge.length - 1) && (
                    <div className="flex items-center justify-center gap-3 mt-9">
                      <span className="w-10 h-px bg-[#D9C7AE]" />
                      <span className="w-1.5 h-1.5 rotate-45 bg-[#BA6D52]/70" />
                      <span className="w-10 h-px bg-[#D9C7AE]" />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <Ornament className="mt-14" />
            <p className="text-center text-sm text-[#5E5147] italic mt-6">
              Ich freue mich darauf, diesen Abend für Sie zu kochen.
            </p>
            <p className="text-center text-[10px] tracking-[0.3em] text-[#BA6D52] mt-4">STUTZ-CUISINE.COM</p>
          </div>
        </div>
      </section>

      {/* ---------- Foto aus der Küche ---------- */}
      <section className="relative overflow-hidden" data-testid="event-foto">
        <div className="absolute inset-0 bg-cover bg-center scale-110 blur-[10px]"
             style={{ backgroundImage: "url('/event-kueche.jpg')" }} />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(14,12,10,0.74)" }} />
        <div className="relative max-w-2xl mx-auto px-6 py-16">
          <img src="/event-kueche.jpg" alt="Lukas Stutz beim Anrichten"
               className="w-full max-w-[540px] mx-auto shadow-[0_30px_70px_-30px_rgba(0,0,0,0.9)]" />
          <p className="text-xs text-[#E2C4B2] text-center mt-5 italic">
            Jeder Gang wird am Abend frisch zubereitet und Teller für Teller angerichtet.
          </p>
        </div>
      </section>

      {/* ---------- Ablauf des Abends ---------- */}
      {(data.ablauf || []).length > 0 && (
        <section className="bg-[#F7EADA] border-y border-[#EBD9C2]" data-testid="event-ablauf-section">
          <div className="max-w-4xl mx-auto px-6 py-16 md:py-20" data-testid="event-ablauf">
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#9C4F31]">Ohne Stress für Sie</p>
              <h2 className="font-serif text-3xl md:text-4xl font-light mt-4">Was Sie am Abend erwartet</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 mt-14">
              {data.ablauf.map((a, i) => (
                <div key={i} data-testid={`event-ablauf-${i}`}>
                  <span className="font-serif text-4xl text-[#BA6D52]/50">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-serif text-xl mt-3">{a.titel}</h3>
                  <p className="text-sm text-[#4A3F36] mt-3 font-light leading-relaxed">{a.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------- Kurze Vorstellung ---------- */}
      {data.vorstellung && (
        <section className="max-w-3xl mx-auto px-6 py-16 md:py-20" data-testid="event-vorstellung">
          <div className="flex flex-col sm:flex-row items-center gap-8 sm:gap-10">
            <img src="/portrait.jpg" alt="Lukas Stutz"
                 onError={(e) => { e.currentTarget.style.display = "none"; }}
                 className="w-32 h-32 md:w-40 md:h-40 object-cover rounded-full border-4 border-white shadow-[0_14px_34px_-20px_rgba(90,52,30,0.7)] shrink-0" />
            <div className="text-center sm:text-left">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#BA6D52]">Ihr Koch</p>
              <h2 className="font-serif text-2xl md:text-3xl font-light mt-3">Lukas Stutz</h2>
              <p className="text-sm text-[#4A3F36] mt-4 font-light leading-relaxed">{data.vorstellung}</p>
            </div>
          </div>
        </section>
      )}

      {/* ---------- Wünsche & Allergien melden ---------- */}
      <section className="bg-[#FFFDF8] border-y border-[#EFE3D2]">
        <div className="max-w-xl mx-auto px-6 py-16" data-testid="event-wuensche">
          <div className="text-center">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#BA6D52]">Damit alles passt</p>
            <h2 className="font-serif text-3xl font-light mt-4">Allergien & Wünsche</h2>
            <p className="text-sm text-[#4A3F36] mt-4 font-light leading-relaxed">
              Vegetarisch, Unverträglichkeiten oder etwas, das Sie nicht mögen?
              Schreiben Sie es hier kurz auf — ich berücksichtige es am Abend.
            </p>
          </div>

          {sent ? (
            <div className="mt-10 bg-[#F3F7EF] border border-[#CFE0C3] px-6 py-8 text-center"
                 data-testid="event-wunsch-danke">
              <Check className="text-[#5C7A4A] mx-auto" size={26} />
              <p className="font-serif text-xl mt-4">Vielen Dank!</p>
              <p className="text-sm text-[#4A3F36] mt-2 font-light">
                Ihre Nachricht ist angekommen. Melden Sie sich gerne nochmal, wenn noch etwas dazukommt.
              </p>
              <button onClick={() => setSent(false)} data-testid="event-wunsch-neu"
                className="mt-6 text-[11px] uppercase tracking-[0.2em] text-[#BA6D52] hover:underline">
                Noch einen Wunsch schicken
              </button>
            </div>
          ) : (
            <div className="mt-10 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className={LBL}>Ihr Name</label>
                  <input value={wunsch.name} onChange={(e) => setWunsch((w) => ({ ...w, name: e.target.value }))}
                    placeholder="z. B. Familie Koch" className={FIELD} data-testid="event-wunsch-name" />
                </div>
                <div>
                  <label className={LBL}>Für wie viele Personen? (optional)</label>
                  <input value={wunsch.personen} onChange={(e) => setWunsch((w) => ({ ...w, personen: e.target.value }))}
                    placeholder="z. B. 2" className={FIELD} data-testid="event-wunsch-personen" />
                </div>
              </div>
              <div>
                <label className={LBL}>Allergien oder Wünsche</label>
                <textarea rows={4} value={wunsch.text} onChange={(e) => setWunsch((w) => ({ ...w, text: e.target.value }))}
                  placeholder="z. B. eine Person isst vegetarisch, keine Nüsse bitte"
                  className={FIELD} data-testid="event-wunsch-text" />
              </div>
              <button onClick={sendWunsch} disabled={sending} data-testid="event-wunsch-senden"
                className="w-full bg-[#BA6D52] hover:bg-[#9C4F31] disabled:opacity-60 text-white px-7 py-4 text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2 transition-colors">
                {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                An Lukas Stutz senden
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ---------- Teilen & Kontakt ---------- */}
      <section className="relative overflow-hidden text-[#FAF3E8]">
        <div className="absolute inset-0 bg-cover bg-center"
             style={{ backgroundImage: "url('/event-bg-gericht.jpg')" }} />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(14,13,10,0.7)" }} />
        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-[#FFFDF8] to-transparent" />
        <div className="relative max-w-3xl mx-auto px-6 pt-24 pb-16 text-center">
          <h2 className="font-serif text-2xl md:text-3xl font-light">Teilen Sie die Vorfreude</h2>
          <p className="text-sm text-[#A89D91] mt-3 font-light">
            Schicken Sie diesen Link an Ihre Gäste — sie sehen dasselbe Menü und denselben Countdown.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <button onClick={share} data-testid="event-share"
              className="inline-flex items-center gap-2 bg-[#BA6D52] text-white hover:bg-[#9C4F31] px-7 py-3.5 text-xs uppercase tracking-[0.2em] transition-colors">
              <Share2 size={15} /> Link weitergeben
            </button>
            <button onClick={copy} data-testid="event-copy"
              className="inline-flex items-center gap-2 border border-[#4A433B] text-[#FAF3E8] hover:border-[#BA6D52] px-7 py-3.5 text-xs uppercase tracking-[0.2em] transition-colors">
              Link kopieren
            </button>
          </div>

          <div className="mt-12 pt-10 border-t border-[#332E27]">
            <p className="text-sm text-[#A89D91] font-light">Noch eine Frage zum Abend?</p>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-5">
              <a href={`https://wa.me/${data.phone_intl}?text=${encodeURIComponent(`Guten Tag Herr Stutz, eine Frage zu unserem Abend am ${dateLong}:`)}`}
                target="_blank" rel="noreferrer" data-testid="event-whatsapp"
                className="inline-flex items-center gap-2 border border-emerald-700/60 text-emerald-300 hover:bg-emerald-900/20 px-6 py-3 text-xs uppercase tracking-[0.2em] transition-colors">
                <MessageCircle size={15} /> WhatsApp
              </a>
              <a href={`tel:+${data.phone_intl}`} data-testid="event-call"
                className="inline-flex items-center gap-2 border border-[#4A433B] text-[#FAF3E8] hover:border-[#BA6D52] px-6 py-3 text-xs uppercase tracking-[0.2em] transition-colors">
                <Phone size={15} /> Anrufen
              </a>
            </div>
          </div>

          <p className="text-[11px] text-[#6E655C] mt-12">
            Lukas Stutz · Privatkoch für Private Dining · stutz-cuisine.com
          </p>
        </div>
      </section>
    </div>
  );
}
